import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import BotAvatar from '../components/BotAvatar';
import BotDetailDialog from '../components/BotDetailDialog';
import BotFormDialog from '../components/BotFormDialog';
import OrgChart from '../components/OrgChart';
import PageHeader from '../components/PageHeader';
import StateBoundary from '../components/StateBoundary';
import { useSession } from '../viewmodels/SessionViewModel';
import { useTeamViewModel } from '../viewmodels/TeamViewModel';

const ORIGIN = { template: 'ready-made team', owner: 'added by you', primary_bot: 'proposed by OrkaCrew' };
const VIEW_KEY = 'orkacrew.teamView';
const STARTERS = [
  {
    key: 'ecommerce',
    title: 'E-commerce team',
    body: 'For a business with an online store: business manager, sales, inventory, support and marketing.',
    action: 'Create e-commerce team',
  },
  {
    key: 'support',
    title: 'Support and marketing team',
    body: 'For a business that serves customers and markets through WhatsApp and analytics, with no store: support, leads and marketing.',
    action: 'Create support team',
  },
  {
    key: 'blank',
    title: 'Start blank',
    body: 'Only OrkaCrew. You add members yourself, with your own roles and instructions.',
    action: 'Start blank',
  },
];

function savedView() {
  try { return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'chart'; } catch (e) { return 'chart'; }
}

function BotNode({
  bot, catalog, depth, childrenOf, isOwner, onEdit,
}) {
  const [open, setOpen] = useState(false);
  const labels = bot.permissions.map((p) => catalog.find((c) => c.action === p)?.label || p);
  const kids = childrenOf.get(bot.id) || [];
  return (
    <Box sx={{ ml: depth ? { xs: 2, sm: 4 } : 0, borderLeft: depth ? 2 : 0, borderColor: 'divider', pl: depth ? { xs: 1.5, sm: 2.5 } : 0 }}>
      <Card sx={{ mb: 1.5, opacity: bot.active ? 1 : 0.7 }}>
        <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <BotAvatar bot={bot} size={44} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={600}>{bot.name}</Typography>
                <Typography color="text.secondary">{bot.role}</Typography>
                {!bot.active && <Chip size="small" label="Inactive" />}
                {bot.is_primary && <Chip size="small" color="primary" variant="outlined" label="Primary bot" />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[bot.reports_to_name ? `Reports to ${bot.reports_to_name}` : 'Top of the team', bot.is_primary ? 'built in' : ORIGIN[bot.created_by]].filter(Boolean).join(' · ')}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {labels.length === 0
                  ? <Chip size="small" variant="outlined" label={bot.is_primary ? 'Organises the team' : 'No access'} />
                  : labels.map((l) => <Chip key={l} size="small" variant="outlined" color="primary" label={l} />)}
              </Stack>
              <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setOpen((o) => !o)}>{open ? 'Hide instructions' : 'Show instructions'}</Button>
              <Collapse in={open}><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{bot.instructions}</Typography></Collapse>
            </Box>
            {isOwner && !bot.is_primary && <Button size="small" onClick={() => onEdit(bot)}>Edit</Button>}
          </Stack>
        </CardContent>
      </Card>
      {kids.map((k) => (
        <BotNode key={k.id} bot={k} catalog={catalog} depth={depth + 1} childrenOf={childrenOf} isOwner={isOwner} onEdit={onEdit} />
      ))}
    </Box>
  );
}

export default function TeamView() {
  const { isOwner } = useSession();
  const vm = useTeamViewModel();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState({ open: false, bot: null });
  const [view, setView] = useState(savedView);
  const [scale, setScale] = useState(1);
  const [selected, setSelected] = useState(null);

  const changeView = (_, next) => {
    if (!next) return;
    setView(next);
    try { window.localStorage.setItem(VIEW_KEY, next); } catch (e) { /* remembering the view is optional */ }
  };

  useEffect(() => {
    if (params.get('new') && isOwner && vm.team) {
      setDialog({ open: true, bot: null });
      setParams({}, { replace: true });
    }
  }, [params, isOwner, vm.team, setParams]);

  const bots = useMemo(() => vm.team?.bots || [], [vm.team]);
  const childrenOf = useMemo(() => {
    const map = new Map();
    bots.forEach((b) => {
      if (b.reports_to_bot_id) map.set(b.reports_to_bot_id, [...(map.get(b.reports_to_bot_id) || []), b]);
    });
    return map;
  }, [bots]);
  const roots = bots.filter((b) => !b.reports_to_bot_id || !bots.some((x) => x.id === b.reports_to_bot_id));
  const onlyPrimary = bots.length <= 1;
  const selectedBot = selected ? bots.find((b) => b.id === selected) : null;

  const edit = (bot) => { vm.clearSaveError(); setSelected(null); setDialog({ open: true, bot }); };
  const save = async (form) => {
    const ok = dialog.bot ? await vm.updateBot(dialog.bot.id, form) : await vm.createBot(form);
    if (ok) setDialog({ open: false, bot: null });
  };

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Your AI team and who reports to whom. Click a team member for details."
        actions={(
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={view} onChange={changeView} aria-label="Team view">
              <ToggleButton value="chart" aria-label="Org chart"><AccountTreeOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />Chart</ToggleButton>
              <ToggleButton value="list" aria-label="List"><ViewListOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />List</ToggleButton>
            </ToggleButtonGroup>
            {isOwner && vm.team && (
              <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => { vm.clearSaveError(); setDialog({ open: true, bot: null }); }}>
                Add team member
              </Button>
            )}
          </Stack>
        )}
      />
      <StateBoundary
        loading={vm.loading}
        error={vm.error}
        onRetry={vm.reload}
        isEmpty={!vm.team}
        empty={{
          icon: <GroupsOutlinedIcon fontSize="large" />,
          title: 'Your team starts with OrkaCrew',
          body: 'Open the chat and tell OrkaCrew about your business. It will suggest a team for you to approve.',
          action: <Button component={RouterLink} to="/chat" variant="contained">Open chat</Button>,
        }}
      >
        {vm.team && (
          <>
            {onlyPrimary && vm.team.template !== 'blank' && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Choose how to start your team</Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Only OrkaCrew is here so far. Pick a ready-made team, or start blank and add your own members. You can change everything later.
                  </Typography>
                  {isOwner ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                      {STARTERS.map((o) => (
                        <Card key={o.key} variant="outlined" sx={{ flex: 1 }}>
                          <CardContent>
                            <Typography fontWeight={600}>{o.title}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 60 }}>{o.body}</Typography>
                            <Button variant={o.key === 'blank' ? 'outlined' : 'contained'} size="small" disabled={vm.saving} onClick={() => vm.createTeam(o.key)}>
                              {vm.saving ? <CircularProgress size={16} color="inherit" /> : o.action}
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Only the account owner can create the team.</Typography>
                  )}
                </CardContent>
              </Card>
            )}
            {onlyPrimary && vm.team.template === 'blank' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your team is blank. Use &quot;Add team member&quot; to build it your way, or ask OrkaCrew in the chat to add members you describe.
              </Alert>
            )}
            {vm.saveError && !dialog.open && <Alert severity="error" sx={{ mb: 2 }}>{vm.saveError}</Alert>}
            {view === 'chart' ? (
              <>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Number on a card = open tasks{isOwner ? '. Drag a member onto another to change who they report to.' : ''}</Typography>
                  <IconButton size="small" aria-label="Zoom out" onClick={() => setScale((s) => Math.max(0.6, +(s - 0.1).toFixed(1)))}><ZoomOutIcon /></IconButton>
                  <IconButton size="small" aria-label="Zoom in" onClick={() => setScale((s) => Math.min(1.2, +(s + 0.1).toFixed(1)))}><ZoomInIcon /></IconButton>
                </Stack>
                <OrgChart
                  bots={bots}
                  counts={vm.counts}
                  selectedId={selected}
                  onSelect={(b) => setSelected(b.id)}
                  scale={scale}
                  onMove={isOwner ? (bot, bossId) => vm.updateBot(bot.id, { reports_to_bot_id: bossId }) : null}
                />
              </>
            ) : roots.map((r) => (
              <BotNode key={r.id} bot={r} catalog={vm.catalog} depth={0} childrenOf={childrenOf} isOwner={isOwner} onEdit={edit} />
            ))}
          </>
        )}
      </StateBoundary>
      <BotDetailDialog bot={selectedBot} catalog={vm.catalog} counts={selectedBot ? vm.counts[selectedBot.id] : null} isOwner={isOwner} onClose={() => setSelected(null)} onEdit={edit} />
      <BotFormDialog
        open={dialog.open}
        bot={dialog.bot}
        bots={bots}
        catalog={vm.catalog}
        saving={vm.saving}
        error={vm.saveError}
        onClose={() => setDialog({ open: false, bot: null })}
        onSave={save}
      />
    </>
  );
}
